// frontend/src/components/layout/AppLayout.jsx
import { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAuth } from '../../hooks/useAuth';
import { useFeatures } from '../../context/FeaturesContext';
import { notificationAPI, marketAPI, socialHubAPI, dwisendPaymentAPI, dwisendShopAPI, dwisendRequestAPI, clientModuleAPI } from '../../services/api';
import {
  LayoutDashboard, Wifi, Wallet, TrendingUp, Users,
  BarChart3, ShieldCheck, Settings, LogOut, ChevronLeft,
  Bell, Menu, X, Radio, Store, UserSquare, Building2,
  ArrowLeftRight, BookOpen, SendHorizonal, ShoppingBasket, Megaphone
} from 'lucide-react';
import { DWILogoHorizontal, DWILogoIcon } from '../DWILogo';
import { OfflineBanner } from '../OfflineBanner';
import { regionalNav as buildRegionalNav } from './regionalNav';
import { isUgandaClient, filterClientNav } from './clientModules';
import { OrderAlertsProvider, useOrderAlerts } from '../../context/OrderAlertsContext';
import OrderAlert from './OrderAlert';

const CUSTOMER_NAV = [
  {
    group: 'MAIN',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    ],
  },
  {
    group: 'FINTECH',
    items: [
      { to: '/wallet',          icon: Wallet,          label: 'Wallet & Payments' },
      { to: '/exchange',        icon: ArrowLeftRight,  label: 'Buy USDT' },
      { to: '/crypto-withdraw', icon: SendHorizonal,   label: 'Withdraw Crypto' },
      { to: '/investments',     icon: TrendingUp,      label: 'Investments' },
    ],
  },
  {
    group: 'SERVICES',
    items: [
      { to: '/isp',      icon: Wifi,     label: 'ISP & Network' },
      { to: '/referral', icon: Users,    label: 'Referral' },
      { to: '/analytics',icon: BarChart3,label: 'Analytics' },
    ],
  },
  {
    group: 'ACCOUNT',
    items: [
      { to: '/kyc',      icon: ShieldCheck, label: 'KYC Verification' },
      { to: '/settings', icon: Settings,    label: 'Settings' },
    ],
  },
];

const AGENT_NAV = [
  {
    group: 'AGENT PORTAL',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Overview' },
      { to: '/agent',     icon: UserSquare,       label: 'Agent Console' },
      { to: '/wallet',    icon: Wallet,           label: 'My Wallet' },
    ],
  },
  {
    group: 'ACCOUNT',
    items: [
      { to: '/settings', icon: Settings, label: 'Settings' },
    ],
  },
];

const MERCHANT_NAV = [
  {
    group: 'MERCHANT PORTAL',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Overview' },
      { to: '/merchant',  icon: Store,           label: 'Merchant Console' },
      { to: '/wallet',    icon: Wallet,          label: 'My Wallet' },
    ],
  },
  {
    group: 'ACCOUNT',
    items: [
      { to: '/kyc',      icon: ShieldCheck, label: 'KYC Verification' },
      { to: '/settings', icon: Settings,    label: 'Settings' },
    ],
  },
];

const PARTNER_NAV = [
  {
    group: 'PARTNERSHIP',
    items: [
      { to: '/partner-portal', icon: Building2, label: 'My Partnership' },
      { to: '/wallet',         icon: Wallet,    label: 'My Wallet' },
    ],
  },
  {
    group: 'ACCOUNT',
    items: [
      { to: '/settings', icon: Settings, label: 'Settings' },
    ],
  },
];

// Accounts based outside Uganda (DRC): the Uganda wallet / investments / ISP
// menu doesn't apply to them (the country gate blocks it) — see regionalNav.js.

const ADMIN_EXTRA = [
  {
    group: 'ADMIN',
    items: [
      { to: '/admin',          icon: Radio,     label: 'Admin Panel'     },
      { to: '/enterprise',     icon: Building2, label: 'Enterprise Core' },
      { to: '/admin-settings', icon: Settings,  label: 'Business Config' },
    ],
  },
];

// Admins get the new-order alerts (banner, chime, badges) — everyone else renders exactly as before.
export default function AppLayout() {
  const { isAdmin } = useAuth();
  return <OrderAlertsProvider enabled={!!isAdmin}><AppLayoutInner /></OrderAlertsProvider>;
}

function AppLayoutInner() {
  const { user, logout, isAdmin, isAgent, isMerchant, isPartner } = useAuth();
  const { features } = useFeatures();
  const [collapsed, setCollapsed]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread]         = useState(0);
  const navigate = useNavigate();

  const fetchUnread = useCallback(async () => {
    try {
      const res = await notificationAPI.getUnreadCount();
      setUnread(res.unreadCount ?? res.count ?? 0);   // the server sends unreadCount
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  // DWI Market: shown when the shop is open (Admin → Market → Settings); admins always see it.
  const [marketOpen, setMarketOpen] = useState(false);
  // …or when seller sign-up (Phase 2) or buy requests (Phase 3) are open — same page, own tabs.
  useEffect(() => { marketAPI.getStatus().then(r => setMarketOpen(!!(r.enabled || r.sellersEnabled || r.requestsEnabled))).catch(() => {}); }, []);
  // Phase 5: unread DWI Market conversations — the user's own, or (admins) those waiting for DWI.
  const [marketUnread, setMarketUnread] = useState(0);
  useEffect(() => {
    if (!isAdmin && !marketOpen) return undefined;
    const load = () => { if (!document.hidden) marketAPI.unread().then(r => setMarketUnread(isAdmin ? (r.dwiUnread || 0) + (r.unread || 0) : (r.unread || 0))).catch(() => {}); };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [isAdmin, marketOpen]);

  // DWI SocialHub: shown only when the socialhub flag is on for this user (no admin bypass).
  const [socialOpen, setSocialOpen] = useState(false);
  useEffect(() => { socialHubAPI.getStatus().then(r => setSocialOpen(!!r.enabled)).catch(() => {}); }, []);

  const investEnabled = !(features && features.investment_projects && features.investment_projects.isEnabled === false);
  const regional = !isAdmin && !!user?.countryCode && user.countryCode !== 'UG';
  const [isApprover, setIsApprover] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);   // DRC shop: shown only once its switch is on for this account
  const [requestsOpen, setRequestsOpen] = useState(false);
  useEffect(() => {
    if (regional) dwisendPaymentAPI.status().then(r => setIsApprover(!!r.youAreApprover)).catch(() => {});
    if (regional) dwisendShopAPI.status().then(r => setShopOpen(!!r.available)).catch(() => {});
    if (regional) dwisendRequestAPI.status().then(r => setRequestsOpen(!!r.available)).catch(() => {});
  }, [regional]);
  const fr = regional && (user?.language === 'fr' || (!user?.language && user?.countryCode === 'CD'));
  // One DwiSend item (Boutique · Mes commandes · Demandes are tabs inside it) + Approbations for approvers.
  const regionalNav = buildRegionalNav({ fr, isApprover, shopOpen, requestsOpen });
  const baseNavRaw = regional ? regionalNav : isPartner ? PARTNER_NAV : isAgent ? AGENT_NAV : isMerchant ? MERCHANT_NAV : CUSTOMER_NAV;
  const baseNav = (!isAdmin && !isPartner && !investEnabled)
    ? baseNavRaw.map(function(g) { return { group: g.group, items: g.items.filter(function(i) { return i.to !== '/investments'; }) }; })
    : baseNavRaw;
  const withMarket = g => ((isAdmin || marketOpen) && !regional && g.group === 'SERVICES'
    ? { group: g.group, items: [{ to: '/market', icon: ShoppingBasket, label: 'DwiSend', badge: marketUnread }, ...g.items] }
    : g);
  const withSocial = g => (socialOpen && g.group === 'SERVICES'
    ? { group: g.group, items: [...g.items, { to: '/socialhub', icon: Megaphone, label: 'SocialHub' }] }
    : g);
  // Uganda client menu switches: switched-off items are hidden (server blocks them too).
  const ugClient = isUgandaClient(user);
  const [clientModules, setClientModules] = useState(null);
  useEffect(() => { if (ugClient) clientModuleAPI.mine().then(r => setClientModules(r.modules || null)).catch(() => {}); }, [ugClient]);
  // Unread new orders (Uganda + DRC) as a red badge on Admin Panel.
  const { count: newOrders } = useOrderAlerts();
  const withOrderBadge = g => (isAdmin && g.group === 'ADMIN'
    ? { group: g.group, items: g.items.map(i => (i.to === '/admin' ? { ...i, badge: newOrders, alert: true } : i)) }
    : g);
  const nav = filterClientNav((isAdmin ? [...baseNav, ...ADMIN_EXTRA] : baseNav).map(withMarket).map(withSocial).map(withOrderBadge), ugClient ? clientModules : null);
  const portal = isPartner ? 'Partner Portal' : isAgent ? 'Agent Portal' : isMerchant ? 'Merchant Portal' : '';

  const Sidebar = () => (
    <div className={clsx(
      'flex flex-col h-full bg-bg-panel border-r border-border transition-all duration-200',
      collapsed ? 'w-[58px]' : 'w-56'
    )}>
      {/* Logo */}
      <div className="flex items-center gap-3 px-3.5 py-4 border-b border-border min-h-[60px]">
        <DWILogoHorizontal collapsed={collapsed} portal={portal} />
      </div>

      {/* Nav links */}
      <nav className="flex-1 overflow-y-auto py-2 px-1.5">
        {nav.map(({ group, items }) => (
          <div key={group} className="mb-1">
            {!collapsed && (
              <p className="text-[9px] font-bold text-text-muted uppercase tracking-widest px-2.5 py-2">{group}</p>
            )}
            {items.map(({ to, icon: Icon, label, badge, alert }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => clsx(
                  'flex items-center gap-2.5 rounded-xl mb-0.5 transition-all duration-100',
                  'text-sm font-medium',
                  collapsed ? 'p-2.5 justify-center' : 'px-2.5 py-2',
                  isActive
                    ? 'bg-cyan/15 text-cyan border border-cyan/30'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-card border border-transparent'
                )}
                title={collapsed ? label : undefined}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {!collapsed && <span>{label}</span>}
                {badge > 0 && <span className={clsx('ml-auto min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center', alert ? 'bg-red text-white animate-pulse' : 'bg-cyan text-bg')} data-testid={alert ? 'order-badge' : undefined}>{badge > 9 ? '9+' : badge}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* User + collapse */}
      <div className="border-t border-border p-2">
        {!collapsed && (
          <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl bg-bg-card mb-1">
            <div className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm text-bg flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #06C8E8, #8B5CF6)' }}>
              {user?.name?.[0] || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-text-primary text-xs font-semibold truncate">{user?.name}</p>
              <p className="text-text-muted text-[10px] capitalize">{user?.role?.replace(/_/g, ' ')}</p>
            </div>
          </div>
        )}
        <button
          onClick={logout}
          className={clsx(
            'flex items-center gap-2 w-full rounded-xl text-text-muted hover:text-red transition-colors',
            collapsed ? 'p-2.5 justify-center' : 'px-2.5 py-2'
          )}
          title="Logout"
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && <span className="text-xs">Logout</span>}
        </button>
        <button
          onClick={() => setCollapsed(c => !c)}
          className={clsx(
            'flex items-center gap-2 w-full rounded-xl text-text-muted hover:text-text-primary transition-colors mt-1',
            collapsed ? 'p-2.5 justify-center' : 'px-2.5 py-2'
          )}
        >
          <ChevronLeft className={clsx('w-4 h-4 transition-transform', collapsed && 'rotate-180')} />
          {!collapsed && <span className="text-xs">Collapse</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-bg overflow-hidden">
      <OfflineBanner />
      <div className="flex flex-1 overflow-hidden">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex flex-col flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="relative flex flex-col h-full w-56 z-10">
            <Sidebar />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="flex-shrink-0 h-14 bg-bg-panel border-b border-border flex items-center justify-between px-4 lg:px-6 gap-4">
          <button
            onClick={() => setMobileOpen(m => !m)}
            className="lg:hidden text-text-muted hover:text-text-primary"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2">
            <DWILogoIcon size={28} />
            <span style={{ fontSize: 13, fontWeight: 800, color: '#F0F6FF', letterSpacing: '-0.02em' }}>
              DWI <span style={{ color: '#00A8DB' }}>FINTECH</span>
            </span>
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-3">
            {/* KYC warning */}
            {user?.kyc?.status !== 'approved' && !isAgent && (
              <NavLink to="/kyc" className="hidden sm:flex items-center gap-1.5 bg-amber/15 border border-amber/30 rounded-lg px-3 py-1.5 text-amber text-xs font-semibold">
                <ShieldCheck className="w-3.5 h-3.5" />
                Complete KYC
              </NavLink>
            )}

            {/* Notifications */}
            <button
              onClick={() => navigate('/notifications')}
              className="relative text-text-muted hover:text-text-primary transition-colors"
              title="Notifications"
            >
              <Bell className="w-5 h-5" />
              {unread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 bg-red rounded-full text-[9px] font-black text-white flex items-center justify-center px-1">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>

            {/* Avatar */}
            <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm text-bg flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #06C8E8, #8B5CF6)' }}>
              {user?.name?.[0] || 'U'}
            </div>
            <div className="hidden sm:block">
              <p className="text-text-primary text-xs font-semibold leading-tight">{user?.name}</p>
              <p className="text-green text-[10px] capitalize">{user?.role?.replace(/_/g, ' ')} {user?.kyc?.status === 'approved' ? '· KYC ✓' : ''}</p>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6 animate-fade-in">
          {isAdmin && <OrderAlert />}
          <Outlet />
        </main>
      </div>
      </div>
    </div>
  );
}
